import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './CalendarDropdown.css';

const VIEW_DAYS = 'days';
const VIEW_MONTHS = 'months';
const VIEW_YEARS = 'years';

const CalendarDropdown = ({ selectedDate, onChange, minDate, maxDate, onClose }) => {
    const { t, i18n } = useTranslation('common');
    const currentLang = i18n.language?.substring(0, 2) || 'pl';

    // Set dayjs locale based on i18n language
    useEffect(() => {
        dayjs.locale(currentLang);
    }, [currentLang]);

    const weekdayLabels = t('datepicker.weekdays', { returnObjects: true });
    const monthNames = t('datepicker.months', { returnObjects: true });

    const today = dayjs();
    const selected = selectedDate ? dayjs(selectedDate) : null;

    const [viewDate, setViewDate] = useState(selected || today);
    const [view, setView] = useState(VIEW_DAYS);
    const [yearRangeStart, setYearRangeStart] = useState(
        Math.floor((selected || today).year() / 12) * 12
    );
    const gridRef = useRef(null);

    const minDay = minDate ? dayjs(minDate).startOf('day') : null;
    const maxDay = maxDate ? dayjs(maxDate).startOf('day') : null;

    const isDateDisabled = useCallback((date) => {
        if (minDay && date.isBefore(minDay, 'day')) return true;
        if (maxDay && date.isAfter(maxDay, 'day')) return true;
        return false;
    }, [minDay, maxDay]);

    const calendarDays = useMemo(() => {
        const startOfMonth = viewDate.startOf('month');

        let startDay = startOfMonth.day() - 1;
        if (startDay < 0) startDay = 6;

        const calStart = startOfMonth.subtract(startDay, 'day');
        const days = [];

        for (let i = 0; i < 42; i++) {
            const date = calStart.add(i, 'day');
            days.push({
                date,
                dayNum: date.date(),
                isCurrentMonth: date.month() === viewDate.month(),
                isToday: date.isSame(today, 'day'),
                isSelected: selected ? date.isSame(selected, 'day') : false,
                isDisabled: isDateDisabled(date),
                isWeekend: date.day() === 0 || date.day() === 6,
            });
        }

        return days;
    }, [viewDate, today, selected, isDateDisabled]);

    const handleDayClick = useCallback((dayInfo) => {
        if (dayInfo.isDisabled) return;
        onChange(dayInfo.date.format('YYYY-MM-DD'));
    }, [onChange]);

    const handlePrevMonth = useCallback(() => {
        setViewDate(prev => prev.subtract(1, 'month'));
    }, []);

    const handleNextMonth = useCallback(() => {
        setViewDate(prev => prev.add(1, 'month'));
    }, []);

    const handleHeaderClick = useCallback(() => {
        if (view === VIEW_DAYS) {
            setView(VIEW_MONTHS);
        } else if (view === VIEW_MONTHS) {
            setYearRangeStart(Math.floor(viewDate.year() / 12) * 12);
            setView(VIEW_YEARS);
        }
    }, [view, viewDate]);

    const handleMonthSelect = useCallback((monthIndex) => {
        setViewDate(prev => prev.month(monthIndex));
        setView(VIEW_DAYS);
    }, []);

    const handleYearSelect = useCallback((year) => {
        setViewDate(prev => prev.year(year));
        setView(VIEW_MONTHS);
    }, []);

    const handleTodayClick = useCallback(() => {
        setViewDate(today);
        setView(VIEW_DAYS);
        if (!isDateDisabled(today)) {
            onChange(today.format('YYYY-MM-DD'));
        }
    }, [today, onChange, isDateDisabled]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        const el = gridRef.current;
        if (el) el.addEventListener('keydown', handleKeyDown);
        return () => { if (el) el.removeEventListener('keydown', handleKeyDown); };
    }, [handleKeyDown]);

    const headerLabel = useMemo(() => {
        return `${monthNames[viewDate.month()]} ${viewDate.year()}`;
    }, [viewDate, monthNames]);

    const renderDaysView = () => (
        <>
            <div className="cdp-weekdays">
                {weekdayLabels.map(day => (
                    <div key={day} className="cdp-weekday">{day}</div>
                ))}
            </div>
            <div className="cdp-days-grid">
                {calendarDays.map((dayInfo, i) => {
                    const classes = ['cdp-day'];
                    if (!dayInfo.isCurrentMonth) classes.push('cdp-day--outside');
                    if (dayInfo.isToday) classes.push('cdp-day--today');
                    if (dayInfo.isSelected) classes.push('cdp-day--selected');
                    if (dayInfo.isDisabled) classes.push('cdp-day--disabled');
                    if (dayInfo.isWeekend) classes.push('cdp-day--weekend');

                    return (
                        <button
                            key={i}
                            type="button"
                            className={classes.join(' ')}
                            onClick={() => handleDayClick(dayInfo)}
                            disabled={dayInfo.isDisabled}
                            tabIndex={dayInfo.isSelected || (i === 0 && !selected) ? 0 : -1}
                            aria-label={dayInfo.date.format('D MMMM YYYY')}
                            aria-selected={dayInfo.isSelected}
                        >
                            {dayInfo.dayNum}
                        </button>
                    );
                })}
            </div>
        </>
    );

    const renderMonthsView = () => (
        <div className="cdp-months-grid">
            {monthNames.map((name, i) => {
                const isCurrentMonth = i === today.month() && viewDate.year() === today.year();
                const isSelected = selected && i === selected.month() && viewDate.year() === selected.year();
                const classes = ['cdp-month-cell'];
                if (isCurrentMonth) classes.push('cdp-month-cell--current');
                if (isSelected) classes.push('cdp-month-cell--selected');

                return (
                    <button
                        key={i}
                        type="button"
                        className={classes.join(' ')}
                        onClick={() => handleMonthSelect(i)}
                    >
                        {name}
                    </button>
                );
            })}
        </div>
    );

    const renderYearsView = () => {
        const years = [];
        for (let y = yearRangeStart; y < yearRangeStart + 12; y++) {
            years.push(y);
        }

        return (
            <div className="cdp-years-grid">
                {years.map(year => {
                    const isCurrent = year === today.year();
                    const isSelected = selected && year === selected.year();
                    const classes = ['cdp-year-cell'];
                    if (isCurrent) classes.push('cdp-year-cell--current');
                    if (isSelected) classes.push('cdp-year-cell--selected');

                    return (
                        <button
                            key={year}
                            type="button"
                            className={classes.join(' ')}
                            onClick={() => handleYearSelect(year)}
                        >
                            {year}
                        </button>
                    );
                })}
            </div>
        );
    };

    const getHeaderDisplay = () => {
        if (view === VIEW_YEARS) {
            return `${yearRangeStart} – ${yearRangeStart + 11}`;
        }
        if (view === VIEW_MONTHS) {
            return `${viewDate.year()}`;
        }
        return headerLabel;
    };

    const getPrevAction = () => {
        if (view === VIEW_DAYS) return handlePrevMonth;
        if (view === VIEW_YEARS) return () => setYearRangeStart(prev => prev - 12);
        return () => setViewDate(prev => prev.subtract(1, 'year'));
    };

    const getNextAction = () => {
        if (view === VIEW_DAYS) return handleNextMonth;
        if (view === VIEW_YEARS) return () => setYearRangeStart(prev => prev + 12);
        return () => setViewDate(prev => prev.add(1, 'year'));
    };

    const getPrevLabel = () => {
        if (view === VIEW_DAYS) return t('datepicker.previousMonth');
        if (view === VIEW_YEARS) return t('datepicker.earlierYears');
        return t('datepicker.previousYear');
    };

    const getNextLabel = () => {
        if (view === VIEW_DAYS) return t('datepicker.nextMonth');
        if (view === VIEW_YEARS) return t('datepicker.laterYears');
        return t('datepicker.nextYear');
    };

    return (
        <div className="cdp-calendar" ref={gridRef} role="dialog" aria-label={t('datepicker.calendar')}>
            <div className="cdp-header">
                <button type="button" className="cdp-nav-btn" onClick={getPrevAction()} aria-label={getPrevLabel()}>
                    <FiChevronLeft />
                </button>
                <button
                    type="button"
                    className="cdp-header-label"
                    onClick={handleHeaderClick}
                    aria-label={view === VIEW_DAYS ? t('datepicker.selectMonth') : view === VIEW_MONTHS ? t('datepicker.selectYear') : undefined}
                    disabled={view === VIEW_YEARS}
                >
                    {getHeaderDisplay()}
                </button>
                <button type="button" className="cdp-nav-btn" onClick={getNextAction()} aria-label={getNextLabel()}>
                    <FiChevronRight />
                </button>
            </div>

            <div className="cdp-body">
                {view === VIEW_DAYS && renderDaysView()}
                {view === VIEW_MONTHS && renderMonthsView()}
                {view === VIEW_YEARS && renderYearsView()}
            </div>

            {view === VIEW_DAYS && (
                <div className="cdp-footer">
                    <button type="button" className="cdp-today-btn" onClick={handleTodayClick}>
                        {t('datepicker.today')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CalendarDropdown;
